import React from 'react';

import type {
  LayoutType,
  PlacementId
} from '@schibsted-nmp/advertising-shared';

import { $placementsMap } from '../atoms/placements.js';
import { RecommendationIds } from '../hooks/RecommendationIds.js';
import { injectSlotInWebComponent } from '../utils/injectSlot.js';
import { shouldCollapse } from '../utils/index.js';

/**
 * Allows to inject a slot into a web component. This is used to get an element from the Light DOM drilling into the Shadow DOM for 1 level deep.
 */
export function AdvertisingNativeSlotComponent(
  props: AdvertisingNativeSlotComponent.Props
) {
  return <AdvertisingNativeSlotComponent.Forwarded {...props} />;
}

export namespace AdvertisingNativeSlotComponent {
  export type Props = React.ComponentProps<typeof Forwarded>;

  export const Forwarded = React.forwardRef<HTMLSlotElement, Forwarded.Props>(
    ({ itemId, categories, initialLayoutType, webComponentName }, ref) => {
      const recommendationIds = RecommendationIds.use(itemId);

      useHideParentElementOnAdLoadFailure(
        recommendationIds,
        itemId,
        webComponentName
      );

      const recircSlotId =
        recommendationIds?.placementId &&
        (`${recommendationIds.placementId}--recirc-slot` as const);

      useInjectSlotInWebComponent(
        recommendationIds,
        recircSlotId,
        categories,
        initialLayoutType,
        webComponentName
      );

      return recommendationIds && <slot ref={ref} name={recircSlotId} />;
    }
  );

  export namespace Forwarded {
    export type Props = {
      /** The id of the item to get the `placementId` for. */
      itemId: PlacementId;

      /** The name of the web component to inject the slot into. */
      webComponentName: string;

      /** The categories of the item to get the `placementId` for. Stringified JSON object. */
      categories?: string | undefined;

      /** The initial layout type of the item to get the `placementId` for. (Grid or List) */
      initialLayoutType?: LayoutType | undefined;
    };
  }

  export function useHideParentElementOnAdLoadFailure(
    recommendationIds: RecommendationIds,
    itemId: PlacementId,
    webComponentName: string
  ) {
    // Hide the parent element if the ad fails to load
    React.useEffect(() => {
      if (!recommendationIds) return () => void 0;

      const { placementId } = recommendationIds;

      return $placementsMap.subscribe((placements) => {
        const placement = placements[placementId];
        if (!placement) return;
        if (
          !['error', 'collapsedOnCollision', 'loaded'].includes(
            placement.status
          )
        )
          return;

        const parentElement =
          typeof document !== 'undefined'
            ? document
                .querySelector(webComponentName)
                ?.shadowRoot?.getElementById(itemId)
            : null;

        if (!parentElement) return;

        // Makes sure the ad does not take up any space in the grid
        // The children will be empty so it won't take up height etc.
        parentElement.style.gridColumn = '1 / -1';
        if (shouldCollapse(placement)) {
          parentElement.classList.add('advt-ad-collapsed');
        }
      });
    }, [recommendationIds, itemId, webComponentName]);
  }

  export function useInjectSlotInWebComponent(
    recommendationIds: RecommendationIds,
    recircSlotId: string | undefined,
    categories: string | undefined,
    initialLayoutType: LayoutType | undefined,
    webComponentName: string
  ) {
    const [recircSlotAdded, setRecircSlotAdded] =
      React.useState<boolean>(false);

    React.useEffect(() => {
      if (recircSlotAdded) return;
      if (!recommendationIds) return;
      if (!recircSlotId) return;

      injectSlotInWebComponent({
        ...recommendationIds,
        categories,
        initialLayoutType,
        webComponentName,
        slotId: recircSlotId
      });

      setRecircSlotAdded(true);
    }, [
      recircSlotAdded,
      recommendationIds,
      categories,
      initialLayoutType,
      webComponentName
    ]);
  }
}
